/**
 * @file Upload 文件上传区
 * @description 拖拽 / 点击选文件；仅管理本地 File 列表（不负责实际上传请求）。
 *
 * @whenToUse 表单附件、头像/文档选择入口。
 * @whenNotToUse 已上传资源的展示列表（用 Table / 自定义列表）；大文件分片上传需业务自行接 API。
 */

import { useId, useRef, useState, type ReactNode } from 'react'
import { FileUp, X } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import { Button } from '@/components/ui/button.tsx'

export type UploadProps = {
  /** 已选文件 */
  value?: File[]
  /** 文件列表变化 */
  onChange?: (files: File[]) => void
  /** 是否允许多选 */
  multiple?: boolean
  /** accept，如 `image/*,.pdf` */
  accept?: string
  /** 禁用 */
  disabled?: boolean
  /** 拖拽区说明 */
  description?: ReactNode
  className?: string
}

function formatSize(size: number): string {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

export function Upload({
  value = [],
  onChange,
  multiple = true,
  accept,
  disabled = false,
  description = '点击或拖拽文件到此处',
  className,
}: UploadProps) {
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const mergeFiles = (incoming: FileList | File[]) => {
    const next = Array.from(incoming)
    if (!multiple) {
      onChange?.(next.slice(0, 1))
      return
    }
    const map = new Map<string, File>()
    for (const file of value) {
      map.set(`${file.name}-${file.size}-${file.lastModified}`, file)
    }
    for (const file of next) {
      map.set(`${file.name}-${file.size}-${file.lastModified}`, file)
    }
    onChange?.(Array.from(map.values()))
  }

  const removeAt = (index: number) => {
    onChange?.(value.filter((_, i) => i !== index))
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <label
        htmlFor={inputId}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-surface px-4 py-6 text-center transition-opacity duration-200',
          dragging && 'border-primary bg-primary/5',
          disabled && 'cursor-not-allowed opacity-50',
        )}
        onDragEnter={(event) => {
          event.preventDefault()
          if (!disabled) setDragging(true)
        }}
        onDragOver={(event) => {
          event.preventDefault()
          if (!disabled) setDragging(true)
        }}
        onDragLeave={(event) => {
          event.preventDefault()
          setDragging(false)
        }}
        onDrop={(event) => {
          event.preventDefault()
          setDragging(false)
          if (disabled) return
          if (event.dataTransfer.files.length > 0) {
            mergeFiles(event.dataTransfer.files)
          }
        }}
      >
        <FileUp className="size-6 text-muted" aria-hidden />
        <span className="text-sm text-foreground">{description}</span>
        <span className="text-xs text-muted">
          {multiple ? '支持多文件' : '仅单文件'}
          {accept ? ` · ${accept}` : ''}
        </span>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          className="sr-only"
          disabled={disabled}
          multiple={multiple}
          accept={accept}
          onChange={(event) => {
            if (event.target.files) mergeFiles(event.target.files)
            event.target.value = ''
          }}
        />
      </label>

      {value.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {value.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className="flex items-center justify-between gap-3 rounded-lg border border-border bg-raised px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-foreground">{file.name}</p>
                <p className="text-xs text-muted">{formatSize(file.size)}</p>
              </div>
              <Button
                variant="ghost"
                disabled={disabled}
                aria-label={`移除 ${file.name}`}
                icon={<X className="size-4" aria-hidden />}
                onClick={() => removeAt(index)}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
